import React, { useRef, useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import FadeInSection from './FadeInSection';
import { useTranslation } from '../i18n/useTranslation';

// Local animation paths (Lottie JSON files in public/json/)
const animationPaths = {
  agencies: '/json/agencies.json',
  cities: '/json/cities.json',
  operators: '/json/operators.json',
  evets: '/json/evets.json',
  corporations: '/json/corporations.json',
  consultancies: '/json/consultancies.json',
  universities: '/json/universities.json'
};

const loadAnimation = async (path) => {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

const AnimationItem = ({ name, animationData }) => {
  const lottieRef = useRef();

  const handleMouseEnter = () => {
    if (lottieRef.current) {
      lottieRef.current.stop();
      lottieRef.current.play();
    }
  };

  return (
    <div className="text-center">
      <div
        className="w-20 h-20 mx-auto cursor-default"
        onMouseEnter={handleMouseEnter}
      >
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          loop={false}
          autoplay={true}
        />
      </div>
      <h3 className="mt-4 font-bold">{name}</h3>
    </div>
  );
};

const SolutionsSection = () => {
  const { t } = useTranslation();
  const [animationsData, setAnimationsData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnimations = async () => {
      try {
        const [
          agencies,
          cities,
          operators,
          evets,
          corporations,
          consultancies,
          universities
        ] = await Promise.all([
          loadAnimation(animationPaths.agencies),
          loadAnimation(animationPaths.cities),
          loadAnimation(animationPaths.operators),
          loadAnimation(animationPaths.evets),
          loadAnimation(animationPaths.corporations),
          loadAnimation(animationPaths.consultancies),
          loadAnimation(animationPaths.universities)
        ]);

        setAnimationsData({
          agencies,
          cities,
          operators,
          evets,
          corporations,
          consultancies,
          universities
        });
        setLoading(false);
      } catch (error) {
        console.error('Error loading animations:', error);
        setLoading(false);
      }
    };

    loadAnimations();
  }, []);

  const solutions = [
    { name: t('solutions.items.agencies'), key: "agencies" },
    { name: t('solutions.items.cities'), key: "cities" },
    { name: t('solutions.items.operators'), key: "operators" },
    { name: t('solutions.items.events'), key: "evets" },
    { name: t('solutions.items.corporations'), key: "corporations" },
    { name: t('solutions.items.consultancies'), key: "consultancies" }
  ];

  const universitySolution = { name: t('solutions.items.universities'), key: "universities" };

  if (loading) {
    return (
      <section className="bg-brand-light-blue text-brand-dark-blue relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-center">
            {t('solutions.title')} <br/>{t('solutions.titleBreak')}
          </h2>
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="text-center text-gray-600">{t('solutions.loadingText')}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-brand-light-blue text-brand-dark-blue relative overflow-hidden" style={{ marginBottom: '-1px' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <FadeInSection animation="fade-in-up" threshold={0.2}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-center pb-2">
            {t('solutions.title')} <br/>{t('solutions.titleBreak')}
          </h2>
        </FadeInSection>
        <FadeInSection animation="fade-in-up" threshold={0.1} delay={200}>
          <div className="mt-16 max-w-5xl mx-auto">
          {/* First row - 4 items */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {solutions.slice(0, 4).map((solution, index) => (
              <AnimationItem
                key={index}
                name={solution.name}
                animationData={animationsData[solution.key]}
              />
            ))}
          </div>
          {/* Second row - 2 items aligned with columns 2 and 3 */}
          <div className="mt-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6">
              <div className="hidden md:block"></div> {/* Empty space for column 1 */}
              {solutions.slice(4).map((solution, index) => (
                <AnimationItem
                  key={index + 4}
                  name={solution.name}
                  animationData={animationsData[solution.key]}
                />
              ))}
              <div className="hidden md:block"></div> {/* Empty space for column 4 */}
            </div>
          </div>
          {/* Third row - University centered */}
          <div className="mt-12 mb-16 flex justify-center">
            <AnimationItem
              name={universitySolution.name}
              animationData={animationsData[universitySolution.key]}
            />
          </div>
        </div>
      </FadeInSection>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-32 sm:h-36 md:h-40 lg:h-44 xl:h-48">
        <svg viewBox="0 0 1534 160" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full">
          <defs>
            <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#93c5fd"/>
              <stop offset="50%" stopColor="#bfdbfe"/>
              <stop offset="100%" stopColor="#dbeafe"/>
            </linearGradient>
          </defs>
          <path d="M0 160H1534V0C1534 0 1150 130 767 130C384 130 0 0 0 0V160Z" fill="url(#curveGradient)"/>
        </svg>
      </div>
    </section>
  );
};

export default SolutionsSection;